import { useState, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { reportQuestion } from '../api/admin';
import { useFocusTrap } from '../hooks/useFocusManagement';

const REASON_VALUES = [
  'incorrect_answer',
  'bad_formatting',
  'wrong_image',
  'outdated',
  'unclear',
  'duplicate',
  'other',
] as const;

interface Props {
  questionId: number;
  onClose: () => void;
}

export default function ReportModal({ questionId, onClose }: Props) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [description, setDescription] = useState('');
  const trapRef = useFocusTrap(true);

  const mutation = useMutation({
    mutationFn: () => reportQuestion(questionId, { reason_type: reason, description }),
    onSuccess: () => {
      onClose();
    },
  });

  // Handle Escape key
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="report-modal-title"
      onClick={onClose}
    >
      <div
        ref={trapRef}
        className="w-full max-w-md rounded-3xl border border-slate-200/70 bg-white p-6 shadow-2xl dark:border-white/10 dark:bg-card-dark"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 mb-4">
          <span className="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-full bg-blue-600 text-white">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 3v1.5M3 21v-6m0 0l2.77-.693a9 9 0 016.208.682l.108.054a9 9 0 006.086.71l3.114-.732a48.524 48.524 0 01-.005-10.499l-3.11.732a9 9 0 01-6.085-.711l-.108-.054a9 9 0 00-6.208-.682L3 4.5M3 15V4.5" />
            </svg>
          </span>
          <div>
            <h3 id="report-modal-title" className="font-bold text-slate-900 dark:text-white">{t('components.reportModal.title', { id: questionId })}</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400">{t('components.reportModal.subtitle')}</p>
          </div>
        </div>

        <div className="space-y-4">
          <fieldset>
            <legend className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              {t('components.reportModal.reasonLabel')}
            </legend>
            <div className="space-y-2" role="radiogroup">
              {REASON_VALUES.map((value) => (
                <label
                  key={value}
                  className={`flex items-center gap-3 rounded-xl border p-3 cursor-pointer transition-colors ${
                    reason === value
                      ? 'border-blue-600 bg-blue-50/60 ring-1 ring-blue-600 dark:border-blue-500 dark:bg-blue-500/10'
                      : 'border-slate-200/70 bg-slate-50/60 hover:bg-slate-100 dark:border-white/10 dark:bg-white/5 dark:hover:bg-white/10'
                  }`}
                >
                  <input
                    type="radio"
                    name="reason"
                    value={value}
                    checked={reason === value}
                    onChange={(e) => setReason(e.target.value)}
                    className="accent-blue-600"
                  />
                  <span className="text-sm text-slate-700 dark:text-slate-300">{t(`components.reportModal.reasons.${value}` as const)}</span>
                </label>
              ))}
            </div>
          </fieldset>

          <div>
            <label htmlFor="report-description" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              {t('components.reportModal.detailsLabel')}
            </label>
            <textarea
              id="report-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              maxLength={1000}
              placeholder={t('components.reportModal.detailsPlaceholder')}
              className="w-full rounded-[10px] border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-blue-600 focus:outline-none focus:ring-[3px] focus:ring-blue-600/15 dark:border-white/10 dark:bg-white/5 dark:text-white"
            />
          </div>

          {mutation.isError && (
            <p className="text-sm text-red-600 dark:text-red-400" role="alert">
              {(mutation.error as Error)?.message?.includes('409')
                ? t('components.reportModal.duplicateError')
                : t('components.reportModal.submitError')}
            </p>
          )}

          {mutation.isSuccess && (
            <p className="text-sm text-green-600 dark:text-green-400" role="status">{t('components.reportModal.submitSuccess')}</p>
          )}
        </div>

        <div className="flex gap-3 justify-end mt-6">
          <button
            onClick={onClose}
            disabled={mutation.isPending}
            className="px-4 py-2 rounded-xl text-sm font-medium border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-white/10 dark:bg-white/5 dark:text-slate-200 dark:hover:bg-white/10 transition-colors"
          >
            {t('components.reportModal.cancel')}
          </button>
          <button
            onClick={() => mutation.mutate()}
            disabled={!reason || mutation.isPending || mutation.isSuccess}
            className="px-4 py-2 rounded-xl text-sm font-medium bg-amber-600 text-white hover:bg-amber-700 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {mutation.isPending && (
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            )}
            {t('components.reportModal.submit')}
          </button>
        </div>
      </div>
    </div>
  );
}
